import { useState } from "react";
import { Home, Building2, Briefcase, Sofa, Warehouse, HelpCircle, BedSingle, BedDouble, Truck, User } from "lucide-react";
import { AddressInput } from "../AddressInput";
import { wInput, wLabel, StepTitle, SelectCard, NavRow } from "./shared";

export const MOVE_TYPES = [
  { id: "Home Move", icon: Home },
  { id: "Apartment/Unit", icon: Building2 },
  { id: "Office Move", icon: Briefcase },
  { id: "Furniture / Single Items", icon: Sofa },
  { id: "Storage Move", icon: Warehouse },
  { id: "Other", icon: HelpCircle },
];

export const PROPERTY_SIZES = [
  { id: "Studio", icon: BedSingle },
  { id: "1 Bedroom", icon: BedSingle },
  { id: "2 Bedroom", icon: BedDouble },
  { id: "3 Bedroom", icon: BedDouble },
  { id: "4 Bedroom", icon: Home },
  { id: "5+ Bedroom", icon: Home },
  { id: "Office", icon: Briefcase },
  { id: "Not Sure", icon: HelpCircle },
];

export const Step1Addresses = ({ data, patch, onNext }) => {
  const [tried, setTried] = useState(false);
  const valid = data.pickup_suburb.trim() && data.drop_suburb.trim();
  const go = () => (valid ? onNext() : setTried(true));
  return (
    <div data-testid="wz-step1">
      <StepTitle title="Pickup & delivery" sub="Where are we moving you from and to?" />
      <div className="space-y-4">
        <div>
          <label className={wLabel}>Pickup From *</label>
          <AddressInput value={data.pickup_suburb} onChange={(v) => patch({ pickup_suburb: v })} placeholder="Address or suburb, e.g. Brunswick or 3056" testid="wz-pickup" inputClassName={wInput} />
        </div>
        <div>
          <label className={wLabel}>Deliver To *</label>
          <AddressInput value={data.drop_suburb} onChange={(v) => patch({ drop_suburb: v })} placeholder="Address or suburb, e.g. Footscray or 3011" testid="wz-drop" inputClassName={wInput} />
        </div>
      </div>
      {tried && !valid && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="wz-step1-error">Please enter both pickup and delivery locations.</p>}
      <NavRow onNext={go} testid="wz-step1-next" />
    </div>
  );
};

export const Step2MoveType = ({ data, patch, onNext, onBack }) => {
  const [tried, setTried] = useState(false);
  const go = () => (data.move_type ? onNext() : setTried(true));
  return (
    <div data-testid="wz-step2">
      <StepTitle title="What are you moving?" />
      <div className="grid gap-3 sm:grid-cols-2">
        {MOVE_TYPES.map((t) => (
          <SelectCard
            key={t.id}
            icon={t.icon}
            label={t.id}
            active={data.move_type === t.id}
            onClick={() => { patch({ move_type: t.id }); setTimeout(onNext, 200); }}
            testid={`wz-type-${t.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          />
        ))}
      </div>
      {tried && !data.move_type && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="wz-step2-error">Please select what you're moving.</p>}
      <NavRow onBack={onBack} onNext={go} testid="wz-step2-next" />
    </div>
  );
};

export const Step3PropertySize = ({ data, patch, onNext, onBack }) => {
  const [tried, setTried] = useState(false);
  const go = () => (data.property_size ? onNext() : setTried(true));
  return (
    <div data-testid="wz-step3">
      <StepTitle title="How big is your move?" sub="This helps us recommend the right truck and estimate your moving time." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {PROPERTY_SIZES.map((s) => (
          <SelectCard
            key={s.id}
            icon={s.icon}
            label={s.id}
            active={data.property_size === s.id}
            onClick={() => { patch({ property_size: s.id }); setTimeout(onNext, 200); }}
            testid={`wz-size-${s.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          />
        ))}
      </div>
      {tried && !data.property_size && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="wz-step3-error">Please select your property size — it sets the truck and time estimate.</p>}
      <NavRow onBack={onBack} onNext={go} testid="wz-step3-next" />
    </div>
  );
};


export const Step5Truck = ({ data, patch, settings, onNext, onBack }) => {
  const trucks = settings?.trucks || [];
  const singleItem = data.move_type === "Furniture / Single Items";
  const bigHouse = ["4 Bedroom", "5+ Bedroom"].includes(data.property_size);
  const nearby = (data.callout_hours ?? 1) <= 1.0;
  const discountEligible = bigHouse && nearby;
  const recommendedId = settings?.sizes?.[data.property_size]?.truck;

  const choose = (truckId, movers) => { patch({ truck_choice: truckId, movers_choice: movers }); setTimeout(onNext, 250); };

  const tierPrice = (t) => (t.unit === "30min" ? `$${t.rate}/30 min` : `$${t.rate}/hr`);

  return (
    <div data-testid="wz-step5">
      <StepTitle title="Choose your truck & movers" sub="Pick the crew size that suits you — or let us recommend." />
      <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="wz-step5-badge">
        <Truck className="h-3 w-3 text-blaze" /> MoversNearby Fleet
      </span>
      <div className="space-y-3">
        {trucks.map((t) => {
          const tiers = (t.tiers || []).filter((tier) => tier.movers > 1 || singleItem);
          return (
            <div key={t.id} className={`rounded-2xl border-2 p-3.5 transition-all duration-200 ${data.truck_choice === t.id ? "border-blaze bg-blaze/[0.06]" : "border-line bg-white"}`} data-testid={`wz-truck-${t.id}`}>
              <div className="flex items-center gap-2">
                <Truck className={`h-4 w-4 shrink-0 ${data.truck_choice === t.id ? "text-blaze" : "text-slate-400"}`} strokeWidth={1.8} />
                <p className="text-sm font-bold text-slate-900">{t.name}</p>
                {recommendedId === t.id && <span className="rounded-full bg-blaze/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blaze">Recommended</span>}
                {t.id === "small" && discountEligible && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-green-700" data-testid="wz-truck-discount">10% Off</span>}
              </div>
              <p className="mt-0.5 text-[10px] text-slate-500">fits up to ~{t.capacity_m3} m³{t.id === "small" && discountEligible ? " · big home + nearby move: small truck deal, 10% off" : ""}</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {tiers.map((tier) => {
                  const active = data.truck_choice === t.id && data.movers_choice === tier.movers;
                  return (
                    <button
                      key={tier.movers}
                      type="button"
                      onClick={() => choose(t.id, tier.movers)}
                      data-testid={`wz-tier-${t.id}-${tier.movers}`}
                      className={`flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all duration-150 active:scale-95 ${
                        active ? "border-blaze bg-blaze text-white" : "border-line bg-white text-slate-700 hover:border-blaze/50"
                      }`}
                    >
                      <span className="flex items-center">
                        {Array.from({ length: tier.movers }).map((_, i) => (
                          <User key={i} className={`h-3 w-3 ${i > 0 ? "-ml-1" : ""} ${active ? "text-white" : "text-blaze"}`} strokeWidth={2.2} />
                        ))}
                      </span>
                      {tier.movers} {tier.movers === 1 ? "Man" : "Men"} · {tierPrice(tier)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <SelectCard
          icon={HelpCircle}
          label="Not Sure — Recommend For Me"
          sub="We'll match the truck and crew to your property size and items"
          active={!data.truck_choice}
          onClick={() => choose("", null)}
          testid="wz-truck-auto"
        />
      </div>
      <NavRow onBack={onBack} onNext={onNext} testid="wz-step5-next" />
    </div>
  );
};
